<script setup lang="ts">
// 369.dev/verify (spec § Verify page). The PDF is hashed in the browser and never uploaded; only { sha256 } goes
// through the relay. Rendered client-only: nothing to server-render, and Web Crypto is a browser API.
import { computed, ref } from 'vue'
import { createEsignApi, EsignApiError, type VerifyHashResponse } from '~/composables/esign/esignApi'
import { useEsignT } from '~/composables/esign/useEsignT'
import { errorCopy } from '~/utils/esign/errorCopy'
import { hashPdfFile } from '~/utils/esign/verifyHash'

definePageMeta({ layout: false })

const { t } = useEsignT()
useSeoMeta({ title: t('verifyPage.title'), description: t('verifyPage.intro') })

const api = createEsignApi()
const checking = ref(false)
const dragging = ref(false)
const error = ref('')
const result = ref<VerifyHashResponse | null>(null)
const input = ref<HTMLInputElement | null>(null)

const match = computed(() => (result.value && result.value.match && !('tombstoned' in result.value) ? result.value : null))
const completedOn = (ms: number) => new Intl.DateTimeFormat(undefined, { dateStyle: 'long', timeStyle: 'short' }).format(new Date(ms))

async function check(file: File | undefined): Promise<void> {
  if (!file || checking.value) return
  checking.value = true
  error.value = ''
  result.value = null
  try {
    const sha256 = await hashPdfFile(file)
    result.value = await api.verifyHash(sha256)
  }
  catch (e) {
    const code = e instanceof EsignApiError ? e.code : (e as Error).message
    error.value = code === 'NOT_PDF' ? t('verifyPage.notPdf') : code === 'TOO_LARGE' ? t('verifyPage.tooLarge') : errorCopy(code)
  }
  finally {
    checking.value = false
    if (input.value) input.value.value = ''
  }
}

function onDrop(event: DragEvent) {
  dragging.value = false
  void check(event.dataTransfer?.files?.[0])
}
</script>

<template>
  <div class="min-h-screen bg-base-200 px-4 py-10 text-base-content">
    <main class="mx-auto max-w-xl">
      <h1 class="text-2xl font-semibold">{{ t('verifyPage.title') }}</h1>
      <p class="mt-2 text-base-content/80">{{ t('verifyPage.intro') }}</p>

      <ClientOnly>
        <label
          data-test="drop-zone"
          class="mt-6 flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center"
          :class="dragging ? 'border-primary bg-primary/5' : 'border-base-content/20 bg-base-100'"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <span class="font-medium">{{ checking ? t('verifyPage.checking') : t('verifyPage.drop') }}</span>
          <span class="btn btn-primary btn-sm mt-3 min-h-11">{{ t('verifyPage.choose') }}</span>
          <input ref="input" data-test="file-input" type="file" accept="application/pdf,.pdf" class="sr-only" @change="check(($event.target as HTMLInputElement).files?.[0])">
        </label>
        <p class="mt-2 text-xs text-base-content/60">{{ t('verifyPage.privacy') }}</p>

        <p v-if="error" role="alert" class="mt-4 text-sm text-[#b42318]">{{ error }}</p>

        <section v-if="match" data-test="match" class="mt-6 rounded-2xl border border-[#067647]/30 bg-[#067647]/5 p-5">
          <p class="font-medium text-[#067647]">{{ match.kind === 'certificate' ? t('verifyPage.match.certificate') : t('verifyPage.match.contract') }}</p>
          <dl class="mt-4 grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
            <dt class="text-base-content/60">{{ t('verifyPage.field.title') }}</dt><dd>{{ match.title }}</dd>
            <dt class="text-base-content/60">{{ t('verifyPage.field.workspace') }}</dt><dd>{{ match.workspace_name }}</dd>
            <dt class="text-base-content/60">{{ t('verifyPage.field.completed') }}</dt><dd>{{ completedOn(match.completed_at) }}</dd>
            <dt class="text-base-content/60">{{ t('verifyPage.field.signers') }}</dt>
            <dd><span v-for="(name, i) in match.signer_names" :key="i" class="block">{{ name }}<span v-if="match.signer_emails_masked[i]" class="text-base-content/60"> · {{ match.signer_emails_masked[i] }}</span></span></dd>
            <dt class="text-base-content/60">{{ t('verifyPage.field.seal') }}</dt><dd>{{ match.seal_level }}</dd>
          </dl>
          <p class="mt-4 text-xs text-base-content/70">{{ t('verifyPage.adobeNote') }}</p>
        </section>

        <p v-else-if="result && result.match" data-test="tombstoned" class="mt-6 rounded-2xl border border-base-content/10 bg-base-100 p-5 text-sm">{{ t('verifyPage.tombstoned') }}</p>
        <p v-else-if="result" data-test="no-match" class="mt-6 rounded-2xl border border-[#b42318]/30 bg-[#b42318]/5 p-5 text-sm text-[#b42318]">{{ t('verifyPage.noMatch') }}</p>
      </ClientOnly>
    </main>
  </div>
</template>
